import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { FUENTE, useTema } from './tema';
import {
  listarFormatos, rellenarWord, generarMinuta, guardarMinuta,
  transcribirAudio, estadoDeVoz
} from '../../services/minutasV2Service';
import { normalizarTranscripcion, unirTranscripciones, comoReloj } from '../../services/transcripcion';
import { datosDeLaCita } from './datosDeLaCita';
import { tituloDesdeNombre, fechaDesdeNombre } from './pistasDelArchivo';
import { codigoDeVia } from './fuenteDeMinuta';

/**
 * SubirTranscripcion — hacer un acta de una reunión que la aplicación no vio.
 *
 * Entra por tres sitios y acaba en el mismo: «Subir transcripción», con el
 * archivo; «Nueva minuta», con el texto pegado; y el audio de la reunión, que
 * se transcribe aquí mismo. Son el mismo trabajo —de unas palabras dichas a un
 * acta guardada— y lo único que cambia es de dónde salen las palabras, así que
 * no son tres pantallas: es una con tres entradas. Tres habrían sido tres
 * sitios donde arreglar el mismo fallo.
 *
 * El audio es el que más lejos llega: de un .mp3 a un acta firmable sin que
 * nadie escriba una línea.
 *
 * Hasta ahora todo salía de Teams: la grabación daba la transcripción, y la
 * cita del calendario daba la fecha, la hora, el lugar y quién convocó. Eso
 * dejaba fuera todo lo demás —la reunión presencial que alguien grabó con el
 * móvil, la de Zoom, la que vino transcrita por otra herramienta, la de hace
 * tres meses cuya grabación ya caducó—, que es justo donde más falta hace un
 * acta y donde hoy se escribe a mano.
 *
 * ── Por qué esta pantalla pregunta cosas ─────────────────────────────────
 *
 * Porque la cabecera de un acta no está en la transcripción. Nadie dice en voz
 * alta «hoy es martes 14, son las diez y media y estamos en la sala 3». Cuando
 * la reunión viene de Teams, eso lo pone el calendario; aquí no hay calendario,
 * así que o se pregunta o el acta sale con los huecos vacíos —o, peor, con lo
 * que el modelo se imagine—.
 *
 * Se pregunta lo mínimo: título y fecha. La hora de inicio y la duración salen
 * de los tiempos de la propia transcripción cuando los trae, que es casi
 * siempre.
 *
 * ── Por qué se enseña lo que se ha entendido ─────────────────────────────
 *
 * Porque los archivos de subtítulos se parecen mucho entre sí y fallan en
 * silencio: un .vtt mal leído no da error, da un acta redactada a partir de
 * números de línea y flechas de tiempo. Enseñar «3 personas, 48 turnos, 52
 * minutos» antes de gastar una llamada al modelo convierte un fallo mudo en una
 * cifra que no cuadra.
 */

const estilos = (C) => ({
  fondo: {
    position: 'fixed', inset: 0, background: 'rgba(3,7,18,.62)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100, padding: 20
  },
  caja: {
    width: 'min(640px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 14,
    boxShadow: '0 22px 60px rgba(0,0,0,.5)', fontFamily: FUENTE, color: C.texto
  },
  cabecera: { padding: '16px 20px 12px', borderBottom: `1px solid ${C.borde}` },
  titulo: { margin: 0, fontSize: 16, fontWeight: 600 },
  bajada: { margin: '6px 0 0', fontSize: 12.5, color: C.tenue, lineHeight: 1.55 },

  cuerpo: { padding: '16px 20px', overflowY: 'auto', flex: 1 },
  bloque: { marginBottom: 18 },
  rotulo: { fontSize: 12.5, fontWeight: 600, color: C.suave, marginBottom: 7 },

  soltar: (encima) => ({
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    padding: '26px 18px', borderRadius: 11, cursor: 'pointer',
    border: `1.5px dashed ${encima ? C.acentoClaro : C.borde}`,
    background: encima ? `${C.acentoClaro}12` : 'transparent',
    color: C.suave, fontSize: 13, textAlign: 'center', transition: 'all .12s'
  }),

  ficha: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
    border: `1px solid ${C.borde}`, borderRadius: 11, background: C.fondoSuave || 'transparent'
  },
  cifras: { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10 },
  cifra: { display: 'flex', flexDirection: 'column', gap: 2 },
  numero: { fontSize: 17, fontWeight: 600, color: C.texto, lineHeight: 1.1 },
  pie: { fontSize: 11.5, color: C.tenue },

  rejilla: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  etiqueta: { display: 'block', fontSize: 12.5, color: C.suave, marginBottom: 5 },
  campo: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    background: C.fondo, border: `1px solid ${C.borde}`, borderRadius: 8,
    color: C.texto, fontSize: 13.5, fontFamily: FUENTE
  },
  muestra: {
    maxHeight: 150, overflowY: 'auto', padding: '10px 12px', marginTop: 8,
    border: `1px solid ${C.borde}`, borderRadius: 9,
    fontSize: 12.5, lineHeight: 1.6, color: C.suave
  },
  quien: { color: C.acentoClaro, fontWeight: 600 },
  cuando: { color: C.tenue, fontVariantNumeric: 'tabular-nums', marginRight: 6 },

  aviso: (malo) => ({
    padding: '10px 12px', borderRadius: 9, fontSize: 12.5, lineHeight: 1.55,
    border: `1px solid ${malo ? C.error : C.borde}`,
    color: malo ? C.error : C.suave
  }),

  pieCaja: {
    display: 'flex', gap: 10, alignItems: 'center',
    padding: '12px 20px', borderTop: `1px solid ${C.borde}`
  },
  boton: {
    padding: '8px 15px', background: 'transparent', border: `1px solid ${C.borde}`,
    borderRadius: 9, color: C.texto, fontSize: 13, fontFamily: FUENTE, cursor: 'pointer'
  },
  botonAcento: { background: C.acento, borderColor: C.acento, color: '#fff', fontWeight: 600 },

  pestanas: { display: 'flex', gap: 6, marginBottom: 14 },
  pestana: (puesta) => ({
    padding: '6px 13px', borderRadius: 8, cursor: 'pointer',
    border: `1px solid ${puesta ? C.acentoClaro : C.borde}`,
    background: puesta ? `${C.acentoClaro}18` : 'transparent',
    color: puesta ? C.acentoClaro : C.suave,
    fontSize: 12.5, fontWeight: puesta ? 600 : 500, fontFamily: FUENTE
  }),
  pegar: {
    width: '100%', boxSizing: 'border-box', minHeight: 150, resize: 'vertical',
    padding: '10px 12px', background: C.fondo, border: `1px solid ${C.borde}`,
    borderRadius: 9, color: C.texto, fontSize: 13, lineHeight: 1.6, fontFamily: FUENTE
  }
});

/** Lo que admite el selector, y lo que se le dice a la gente. */
const EXTENSIONES = '.txt,.vtt,.srt,.json';

/** Hoy, en el formato que quiere un <input type="date">. */
function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * @param {Object} props
 * @param {string} props.userId
 * @param {'archivo'|'pegar'} props.entradaInicial - por dónde se entró
 * @param {Function} props.onCerrar
 * @param {Function} props.onCreada - recibe { minuteId } del acta recién hecha
 */
const SubirTranscripcion = ({ userId, entradaInicial = 'archivo', onCerrar, onCreada }) => {
  const { C, aviso } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const entrada = useRef(null);
  const entradaAudio = useRef(null);
  const [encima, setEncima] = useState(false);

  // Se abre por donde se pulsó, pero se puede cambiar: quien iba a pegar y al
  // final tiene el archivo a mano no debería cerrar y volver a empezar.
  const [via, setVia] = useState(entradaInicial);
  // ── Varios archivos de la MISMA reunion ──────────────────────────────
  //
  // Una reunion no siempre deja una sola transcripcion: se cae el internet, se
  // corta la grabacion, alguien la vuelve a iniciar. Generar el acta con una
  // sola da un acta a la que le falta media junta, y lo peor es que no se nota
  // — sale bien escrita y completa de aspecto, sin la mitad de los acuerdos.
  //
  // Se guardan los archivos y sus contenidos EN ORDEN. El orden importa y lo
  // decide quien los sube: nada dentro de un .vtt dice cual fue antes, porque
  // los dos empiezan en 00:00.
  const [archivos, setArchivos] = useState([]);
  const [piezas, setPiezas] = useState([]);
  // El primero manda para el titulo y la fecha, como cuando habia uno solo.
  const archivo = archivos[0] || null;
  const [crudo, setCrudo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [fecha, setFecha] = useState(hoyISO);
  const [lugar, setLugar] = useState('');
  const [formatos, setFormatos] = useState([]);
  const [formatoSel, setFormatoSel] = useState('');

  // ── El audio ──────────────────────────────────────────────────────────
  //
  // `voz` dice si este despliegue puede transcribir. Se pregunta al abrir y no
  // al subir: enterarse de que no se puede DESPUES de esperar a que suban cien
  // megas es la peor manera posible.
  const [voz, setVoz] = useState(null);
  const [audio, setAudio] = useState(null);
  const [transcribiendo, setTranscribiendo] = useState(false);

  const [trabajando, setTrabajando] = useState(false);
  const [paso, setPaso] = useState('');
  const [error, setError] = useState(null);

  // ── Los formatos ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    listarFormatos(userId)
      .then(lista => {
        setFormatos(lista || []);
        // El primero por defecto: sin formato el acta sale como texto corrido,
        // sin zonas que corregir, sin Word y sin poder mandarse a firmar. Es una
        // salida legítima, pero no la que casi nadie quiere.
        if (lista && lista.length > 0) setFormatoSel(String(lista[0].formatId));
      })
      .catch(() => setFormatos([]));
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    estadoDeVoz(userId).then(setVoz).catch(() => setVoz({ ready: false }));
  }, [userId]);

  // ── Lo que se ha entendido del archivo ────────────────────────────────
  //
  // Se calcula aquí y no al generar: es lo que se enseña para poder decir «esto
  // no es lo que subí» antes de gastar una llamada al modelo.
  const leida = useMemo(() => (crudo ? normalizarTranscripcion(crudo) : null), [crudo]);

  const leerArchivo = (f) => new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onload = () => resolver(String(lector.result || ''));
    lector.onerror = () => rechazar(new Error(`No se pudo leer «${f.name}».`));
    lector.readAsText(f);
  });

  /**
   * Suma archivos a los que ya hay. No los sustituye.
   *
   * Sumar y no sustituir es lo que permite soltar el segundo .vtt encima del
   * primero, que es como se usa esto: uno descubre que le falta media reunión
   * DESPUÉS de haber subido la primera parte.
   *
   * Con uno solo, `crudo` es el archivo tal cual — exactamente como antes. Con
   * varios, es la unión ya hecha: así todo lo de abajo —lo que se enseña, lo
   * que va al modelo, lo que se guarda— sigue leyendo un solo sitio y no se
   * entera de que hubo dos.
   */
  const tomar = useCallback(async (nuevos) => {
    const lista = Array.from(nuevos || []).filter(Boolean);
    if (lista.length === 0) return;
    setError(null);

    try {
      const textos = await Promise.all(lista.map(leerArchivo));

      const todosLosArchivos = [...archivos, ...lista];
      const todasLasPiezas = [...piezas, ...textos];

      setArchivos(todosLosArchivos);
      setPiezas(todasLasPiezas);
      setCrudo(todasLasPiezas.length === 1
        ? todasLasPiezas[0]
        : JSON.stringify(unirTranscripciones(todasLasPiezas)));

      const primero = todosLosArchivos[0];
      if (!titulo.trim()) setTitulo(tituloDesdeNombre(primero.name));
      const delNombre = fechaDesdeNombre(primero.name);
      if (delNombre) setFecha(delNombre);
    } catch (e) {
      setError(e.message || 'No se pudo leer el archivo.');
    }
  }, [titulo, archivos, piezas]);

  /** Quita uno de la lista y rehace la unión con los que quedan. */
  const quitarArchivo = useCallback((i) => {
    const quedanArchivos = archivos.filter((_, n) => n !== i);
    const quedanPiezas = piezas.filter((_, n) => n !== i);
    setArchivos(quedanArchivos);
    setPiezas(quedanPiezas);
    setCrudo(
      quedanPiezas.length === 0 ? ''
        : quedanPiezas.length === 1 ? quedanPiezas[0]
          : JSON.stringify(unirTranscripciones(quedanPiezas))
    );
  }, [archivos, piezas]);

  /**
   * Transcribe el audio de la reunión.
   *
   * Lo que sale ocupa el mismo sitio que un archivo de subtítulos o un texto
   * pegado: de aquí en adelante todo es igual. Esa es la razón de que el audio
   * sea una entrada más y no una pantalla aparte.
   */
  const tomarAudio = useCallback(async (f) => {
    if (!f || transcribiendo) return;
    setError(null);
    setAudio(f);
    setCrudo('');
    setTranscribiendo(true);
    try {
      const leido = await transcribirAudio(userId, f);
      if (!leido || !leido.texto) {
        setError('No se reconoció nada en el audio. ¿Está en silencio, o es música?');
        return;
      }
      // ── Se guarda lo que Azure separó, no su resumen en texto ──────────
      //
      // Aquí se guardaba solo `leido.texto`, que son líneas «Persona 1: …».
      // Y eso tiraba por la borda el trabajo ya hecho: Azure SÍ separa a los
      // hablantes —la diarización está pedida—, pero sus turnos se perdían y
      // después se intentaba deducirlos otra vez del texto plano. Ningún
      // patrón reconoce ese formato, así que salían CERO hablantes.
      //
      // El resultado, en una prueba real: una reunión de dos personas en la
      // que todo aparecía dicho por una sola, la pantalla avisando de que «no
      // se distingue quién habla», y el acta con un único asistente.
      //
      // Guardando el objeto entero, `normalizarTranscripcion` lo relee con sus
      // turnos y sus tiempos —ahora conoce nuestras propias claves— y al
      // modelo le sigue llegando la conversación limpia, no el JSON.
      setCrudo(JSON.stringify(leido));
      if (!titulo.trim()) setTitulo(tituloDesdeNombre(f.name));
      const delNombre = fechaDesdeNombre(f.name);
      if (delNombre) setFecha(delNombre);
    } catch (e) {
      setError(e.mensajeUsuario || e.message || 'No se pudo transcribir el audio.');
      setAudio(null);
    } finally {
      setTranscribiendo(false);
    }
  }, [transcribiendo, userId, titulo]);

  /**
   * Hace el acta y la guarda.
   *
   * Guardar es la mitad del trabajo, y la que faltaba: una minuta que solo se
   * puede descargar no entra en el listado, no tiene folio, no se corrige por
   * zonas, no va a Planner y no se manda a firmar. Al terminar se abre, que es
   * lo que se esperaba desde el principio.
   */
  const crear = useCallback(async () => {
    if (!leida || trabajando) return;
    setTrabajando(true);
    setError(null);
    try {
      const nombre = titulo.trim() || tituloDesdeNombre(archivo && archivo.name) || 'Acta sin título';
      const inicio = fecha ? new Date(`${fecha}T00:00:00`) : null;

      // La cabecera del acta no está en la transcripción. Se arma una cita con
      // lo que sí se sabe —lo que se acaba de escribir arriba y la duración que
      // dicen los tiempos del archivo— y se manda igual que si viniera del
      // calendario, para que el modelo rellene la cabecera como siempre.
      const cita = datosDeLaCita({
        titulo: nombre,
        ubicacion: lugar.trim() || null,
        inicio,
        fin: inicio && leida.duracion ? new Date(inicio.getTime() + leida.duracion * 1000) : null,
        asistentes: leida.hablantes
      });

      // Al modelo va la conversación ya limpia, no el archivo.
      //
      // Un .vtt son dos tercios de cabecera, números de cue, flechas de tiempo
      // y etiquetas; mandarlo crudo es pagar esos tokens y además pedirle al
      // modelo que adivine qué parte de lo que lee es lo que se dijo. Lo que se
      // GUARDA sí es el archivo entero: de ahí salen los tiempos, y sin ellos la
      // pantalla de hablantes y el salto a un minuto dejarían de funcionar.
      const paraElModelo = leida.texto || crudo;

      let html, meta, verificacion = null;

      if (formatoSel) {
        setPaso('Volcando el acta en tu formato…');
        const relleno = await rellenarWord({
          formatId: Number(formatoSel),
          transcripcion: paraElModelo,
          cita,
          userId
        });
        html = relleno.html;
        // Con el contenido por zona dentro: es lo que permite reabrir el acta,
        // rehacer el .docx y corregirla. Sin esto nacería muerta.
        meta = { ...(relleno.meta || {}), valores: relleno.valores, regiones: relleno.regiones };
      } else {
        setPaso('Redactando el acta…');
        const hecha = await generarMinuta({ transcripcion: paraElModelo, formatId: null, userId });
        html = hecha.html;
        meta = hecha.meta;
        verificacion = hecha.verificacion;
      }

      setPaso('Guardando…');
      const guardada = await guardarMinuta({
        userId,
        titulo: nombre,
        html,
        // La transcripción viaja con el acta: es de donde salen los hablantes y
        // la búsqueda de «¿quién dijo esto?» al reabrirla.
        transcripcion: crudo,
        formatId: formatoSel ? Number(formatoSel) : null,
        meetingDate: inicio ? inicio.toISOString() : null,
        // De dónde salió. Las que vienen de Teams llevan el id de la reunión;
        // estas no tienen ninguna, y marcarlas permite distinguirlas después.
        reunionId: `transcripcion-${Date.now()}`,
        // Cual de las tres vias se uso. Es lo unico que distingue despues un
        // acta nacida de un audio de una nacida de un .vtt, y es lo que permite
        // ofrecer borrarla: estas no tienen reunion detras de la que rehacerlas.
        fuente: codigoDeVia(via),
        verificacion,
        meta
      });

      onCreada({ minuteId: guardada.minuteId });
    } catch (e) {
      setError(e.mensajeUsuario || e.message || 'No se pudo crear el acta.');
      setTrabajando(false);
      setPaso('');
    }
  }, [leida, trabajando, titulo, archivo, fecha, lugar, formatoSel, crudo, via, userId, onCreada]);

  // Un archivo que no se entiende: se dice por qué y qué hacer, en vez de
  // dejar generar un acta a partir de basura.
  const ilegible = Boolean(crudo) && (!leida || (leida.turnos.length === 0 && !leida.texto));

  return (
    <div style={S.fondo} onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }}>
      <div style={S.caja} role="dialog" aria-modal="true" aria-label="Subir una transcripción">
        <div style={S.cabecera}>
          <h3 style={S.titulo}>
            {via === 'pegar' ? 'Nueva minuta'
              : via === 'audio' ? 'Acta desde la grabación'
                : 'Subir transcripción'}
          </h3>
          <p style={S.bajada}>
            Para una reunión que no está en Teams. De la transcripción sale el acta
            completa: se guarda, se corrige por zonas y se puede mandar a firmar, igual
            que las demás.
          </p>
        </div>

        <div style={S.cuerpo}>
          {/* ── De dónde sale la transcripción ── */}
          <div style={S.pestanas} role="tablist">
            <button
              style={S.pestana(via === 'archivo')}
              onClick={() => setVia('archivo')}
              role="tab" aria-selected={via === 'archivo'}
              disabled={trabajando}
            >
              Desde un archivo
            </button>
            <button
              style={S.pestana(via === 'pegar')}
              onClick={() => setVia('pegar')}
              role="tab" aria-selected={via === 'pegar'}
              disabled={trabajando}
            >
              Pegar el texto
            </button>
            {/* Solo si este despliegue puede. Ofrecer un botón que va a fallar
                es peor que no ofrecerlo. */}
            {voz && voz.ready && (
              <button
                style={S.pestana(via === 'audio')}
                onClick={() => setVia('audio')}
                role="tab" aria-selected={via === 'audio'}
                disabled={trabajando}
              >
                Desde el audio
              </button>
            )}
          </div>

          {via === 'audio' && (
            <div style={S.bloque}>
              {!audio ? (
                <div
                  style={S.soltar(encima)}
                  onClick={() => entradaAudio.current && entradaAudio.current.click()}
                  onDragOver={(e) => { e.preventDefault(); setEncima(true); }}
                  onDragLeave={() => setEncima(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setEncima(false);
                    tomarAudio(e.dataTransfer.files && e.dataTransfer.files[0]);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') entradaAudio.current.click(); }}
                >
                  <span style={{ fontSize: 26 }} aria-hidden="true">🎙️</span>
                  <strong style={{ color: C.texto, fontSize: 13.5 }}>
                    Arrastra la grabación, o pulsa para elegirla
                  </strong>
                  <span style={{ fontSize: 12, color: C.tenue }}>
                    {(voz.formats || []).join(', ')} · hasta{' '}
                    {Math.round((voz.maxBytes || 0) / 1024 / 1024)} MB
                  </span>
                </div>
              ) : (
                <div style={S.ficha}>
                  <span style={{ fontSize: 20 }} aria-hidden="true">🎙️</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {audio.name}
                    </div>
                    <div style={S.pie}>
                      {(audio.size / 1024 / 1024).toFixed(1)} MB
                      {transcribiendo ? ' · escuchando…' : (crudo ? ' · transcrito' : '')}
                    </div>
                  </div>
                  <button
                    style={S.boton}
                    onClick={() => { setAudio(null); setCrudo(''); }}
                    disabled={trabajando || transcribiendo}
                  >
                    Cambiar
                  </button>
                </div>
              )}

              {transcribiendo && (
                <p style={{ ...S.pie, marginTop: 8, lineHeight: 1.55 }}>
                  Transcribiendo. Tarda alrededor de un minuto por cada veinte de
                  reunión; no cierres esta ventana.
                </p>
              )}

              <input
                ref={entradaAudio}
                type="file"
                accept="audio/*,video/mp4,.m4a,.mp3,.wav,.ogg,.opus,.flac,.webm,.amr,.wma"
                style={{ display: 'none' }}
                onChange={(e) => tomarAudio(e.target.files && e.target.files[0])}
              />
            </div>
          )}

          {via === 'pegar' && (
            <div style={S.bloque}>
              <textarea
                style={S.pegar}
                value={crudo}
                onChange={(e) => { setCrudo(e.target.value); setArchivos([]); setPiezas([]); }}
                placeholder={'Pega aquí lo que se dijo.\n\nVale el texto corrido, «Ana: …» '
                  + 'línea a línea, o el contenido de un .vtt o del JSON de Teams.'}
                aria-label="Transcripción"
              />
              <p style={{ ...S.pie, marginTop: 6 }}>
                Cuanto mejor marque quién dice cada cosa, mejor reparte el acta los
                acuerdos.
              </p>
            </div>
          )}

          {/* ── El archivo ── */}
          {via === 'archivo' && (
          <div style={S.bloque}>
            {/* Los que ya estan, en orden. El numero delante no es adorno:
                con dos partes de la misma reunion, el orden decide como se
                lee la conversacion y nada dentro de los archivos lo dice. */}
            {archivos.map((f, i) => (
              <div key={`${f.name}-${i}`} style={{ ...S.ficha, marginBottom: 8 }}>
                <span style={{ fontSize: 20 }} aria-hidden="true">📄</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {archivos.length > 1 && (
                      <span style={{ color: C.tenue, fontWeight: 400 }}>{i + 1}. </span>
                    )}
                    {f.name}
                  </div>
                  <div style={S.pie}>{(f.size / 1024).toFixed(0)} KB</div>
                </div>
                <button style={S.boton} onClick={() => quitarArchivo(i)} disabled={trabajando}>
                  Quitar
                </button>
              </div>
            ))}

            {/* La zona de soltar NO desaparece al haber uno: es lo que permite
                añadir la segunda parte sin empezar de cero. */}
            <div
              style={S.soltar(encima)}
              onClick={() => entrada.current && entrada.current.click()}
              onDragOver={(e) => { e.preventDefault(); setEncima(true); }}
              onDragLeave={() => setEncima(false)}
              onDrop={(e) => {
                e.preventDefault();
                setEncima(false);
                tomar(e.dataTransfer.files);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') entrada.current.click(); }}
            >
              <span style={{ fontSize: 26 }} aria-hidden="true">⬆️</span>
              <strong style={{ color: C.texto, fontSize: 13.5 }}>
                {archivos.length === 0
                  ? 'Arrastra el archivo, o pulsa para elegirlo'
                  : 'Añade otra parte de la misma reunión'}
              </strong>
              <span style={{ fontSize: 12, color: C.tenue }}>
                {archivos.length === 0
                  ? 'Subtítulos de Teams o Zoom (.vtt, .srt), texto (.txt) o el JSON de Teams'
                  : 'Si la grabación se cortó y la reiniciaste, sube aquí la segunda parte'}
              </span>
            </div>

            {/* Lo que hay que decir y no se puede deducir mirando el acta.
                Entre que se corto una grabacion y empezo la siguiente paso un
                rato real que los archivos no cuentan: los minutos de la
                segunda parte son «desde que empezo la primera», no la hora. */}
            {archivos.length > 1 && (
              <div style={{ ...aviso('info'), marginTop: 10 }}>
                Se unirán {archivos.length} partes en una sola acta, en este orden.
                Los minutos de las partes siguientes se cuentan desde que empezó la
                primera, así que son aproximados: entre una y otra pasó un rato que
                los archivos no registran.
              </div>
            )}

            <input
              ref={entrada}
              type="file"
              accept={EXTENSIONES}
              multiple
              style={{ display: 'none' }}
              onChange={(e) => tomar(e.target.files)}
            />
          </div>
          )}

          {ilegible && (
            <div style={{ ...S.bloque, ...S.aviso(true) }}>
              No se encontró nada que parezca una conversación. Si es un .vtt o un .srt,
              puede que esté incompleto; si es un JSON, que no sea el de la transcripción
              de Teams.
            </div>
          )}

          {/* ── Lo que se ha entendido ── */}
          {leida && !ilegible && (
            <div style={S.bloque}>
              <div style={S.rotulo}>Lo que se ha entendido</div>
              <div style={S.cifras}>
                <div style={S.cifra}>
                  <span style={S.numero}>{leida.hablantes.length || '—'}</span>
                  <span style={S.pie}>{leida.hablantes.length === 1 ? 'persona' : 'personas'}</span>
                </div>
                <div style={S.cifra}>
                  <span style={S.numero}>{leida.turnos.length || '—'}</span>
                  <span style={S.pie}>intervenciones</span>
                </div>
                <div style={S.cifra}>
                  <span style={S.numero}>{leida.duracion ? comoReloj(leida.duracion) : '—'}</span>
                  <span style={S.pie}>duración</span>
                </div>
                <div style={S.cifra}>
                  <span style={S.numero}>{(crudo.length / 1000).toFixed(0)}k</span>
                  <span style={S.pie}>caracteres</span>
                </div>
              </div>

              {/* Las primeras líneas, tal como se han leído. Es lo que delata un
                  archivo mal interpretado: si aquí sale «00:00:05.447 -->», no
                  se entendió nada, y vale más verlo ahora que en el acta. */}
              {leida.turnos.length > 0 ? (
                <div style={S.muestra}>
                  {leida.turnos.slice(0, 6).map((t, i) => (
                    <div key={i} style={{ marginBottom: 5 }}>
                      {t.inicio != null && <span style={S.cuando}>{comoReloj(t.inicio)}</span>}
                      {t.hablante && <span style={S.quien}>{t.hablante}: </span>}
                      {t.texto.slice(0, 160)}{t.texto.length > 160 ? '…' : ''}
                    </div>
                  ))}
                </div>
              ) : (
                // Texto corrido, sin turnos que separar. Se enseña igual: la
                // muestra es lo que delata un archivo mal leído, y dejarla fuera
                // justo en el caso más común —pegar texto a secas— quitaría la
                // comprobación donde más falta hace.
                <div style={S.muestra}>
                  {leida.texto.slice(0, 400)}{leida.texto.length > 400 ? '…' : ''}
                </div>
              )}

              {leida.hablantes.length === 0 && (
                <div style={{ ...S.aviso(false), marginTop: 10 }}>
                  No se distingue quién habla: el archivo no trae nombres. El acta se
                  redactará igual, pero sin repartir las intervenciones.
                </div>
              )}

              {via === 'audio' && leida.hablantes.length > 0 && (
                <div style={{ ...S.aviso(false), marginTop: 10 }}>
                  La grabación dice cuántas personas hablan, pero no cómo se llaman: van
                  como «Persona 1», «Persona 2». Al abrir el acta se cambian por los
                  nombres de verdad en la tabla de asistentes.
                </div>
              )}
            </div>
          )}

          {/* ── Lo que la transcripción no dice ── */}
          {leida && !ilegible && (
            <div style={S.bloque}>
              <div style={S.rotulo}>De la reunión</div>
              <div style={{ marginBottom: 12 }}>
                <label style={S.etiqueta} htmlFor="tr-titulo">Título</label>
                <input
                  id="tr-titulo"
                  style={S.campo}
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  placeholder="Reunión de seguimiento"
                />
              </div>
              <div style={S.rejilla}>
                <div>
                  <label style={S.etiqueta} htmlFor="tr-fecha">Fecha</label>
                  <input
                    id="tr-fecha"
                    type="date"
                    style={S.campo}
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                  />
                </div>
                <div>
                  <label style={S.etiqueta} htmlFor="tr-lugar">Lugar</label>
                  <input
                    id="tr-lugar"
                    style={S.campo}
                    value={lugar}
                    onChange={(e) => setLugar(e.target.value)}
                    placeholder="Sala 3, Zoom…"
                  />
                </div>
              </div>
              <p style={{ ...S.pie, marginTop: 8, lineHeight: 1.55 }}>
                Esto no está en la transcripción —nadie dice la fecha en voz alta— y es
                la cabecera del acta. La hora y la duración salen de los tiempos del
                archivo.
              </p>
            </div>
          )}

          {/* ── El formato ── */}
          {leida && !ilegible && (
            <div style={S.bloque}>
              <div style={S.rotulo}>Formato del acta</div>
              <select
                style={S.campo}
                value={formatoSel}
                onChange={(e) => setFormatoSel(e.target.value)}
                aria-label="Formato del acta"
              >
                {formatos.map(f => (
                  <option key={f.formatId} value={f.formatId}>{f.name}</option>
                ))}
                <option value="">Sin formato — solo texto</option>
              </select>
              {!formatoSel && (
                <p style={{ ...S.pie, marginTop: 7, lineHeight: 1.55 }}>
                  Sin formato el acta sale como texto: no se podrá corregir por zonas, ni
                  verla en Word, ni mandarla a firmar.
                </p>
              )}
            </div>
          )}

          {error && <div style={S.aviso(true)}>{error}</div>}
        </div>

        <div style={S.pieCaja}>
          <span style={{ flex: 1, fontSize: 12.5, color: C.tenue }}>
            {trabajando ? paso
              : transcribiendo ? 'Escuchando la grabación…'
                : (leida && !ilegible ? 'Se abrirá al terminar.' : '')}
          </span>
          <button style={S.boton} onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button
            style={{ ...S.boton, ...S.botonAcento, opacity: (!leida || ilegible) ? .5 : 1 }}
            onClick={crear}
            disabled={!leida || ilegible || trabajando || transcribiendo}
          >
            {trabajando ? 'Creando…' : 'Crear el acta'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubirTranscripcion;
